import { useState } from "react";
import { Mic, X } from "lucide-react";
import { SHORTCUTS, wordStats } from "./model";

const inp = "w-full h-9 rounded-md border border-slate-300 bg-white px-2.5 text-[13px] text-slate-900 outline-none focus:border-[#1a73e8]";
const primary = "h-9 px-4 rounded-full bg-[#1a73e8] text-white text-[13px] font-medium hover:bg-[#1765cc] disabled:opacity-40";
const ghost = "h-9 px-3 rounded-full text-[#1a73e8] text-[13px] font-medium hover:bg-[#e8f0fe]";

export function Modal({ title, children, onClose, testid, wide }) {
  return (
    <div className="absolute inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose} data-testid={`${testid}-overlay`}>
      <div data-testid={testid} onClick={(e) => e.stopPropagation()} className={`bg-white rounded-2xl shadow-2xl ${wide ? "w-[520px]" : "w-[400px]"} max-w-full max-h-full overflow-y-auto`}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2"><h3 className="text-[16px] text-slate-900">{title}</h3><button data-testid={`${testid}-close`} onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-4 h-4" /></button></div>
        <div className="px-5 pb-5 text-[13px] text-slate-800">{children}</div>
      </div>
    </div>
  );
}

function Footer({ onClose, onOk, okLabel = "OK", disabled, testid }) {
  return <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className={ghost} data-testid={`${testid}-cancel`}>Cancel</button><button onClick={onOk} disabled={disabled} className={primary} data-testid={`${testid}-ok`}>{okLabel}</button></div>;
}

export function PageSetupDialog({ doc, config, act, onClose }) {
  const [p, setP] = useState({ ...doc.page });
  return (
    <Modal title="Page setup" onClose={onClose} testid="studio-pagesetup">
      <p className="text-slate-500 mb-2">Orientation</p>
      <div className="flex gap-4 mb-4">{["portrait", "landscape"].map((o) => <label key={o} className="flex items-center gap-2 capitalize"><input type="radio" data-testid={`studio-pagesetup-${o}`} checked={p.orientation === o} onChange={() => setP({ ...p, orientation: o })} className="accent-[#1a73e8]" /> {o}</label>)}</div>
      <label className="block text-slate-500 mb-1">Paper size</label>
      <select data-testid="studio-pagesetup-size" value={p.size} onChange={(e) => setP({ ...p, size: e.target.value })} className={`${inp} mb-4`}>{config.pageSizes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
      <label className="block text-slate-500 mb-1">Margins (inches, all sides)</label>
      <select data-testid="studio-pagesetup-margin" value={p.margin} onChange={(e) => setP({ ...p, margin: Number(e.target.value) })} className={inp}>{config.margins.map((m) => <option key={m} value={m}>{`${m}"`}</option>)}</select>
      <Footer testid="studio-pagesetup" onClose={onClose} onOk={() => { act.setDoc({ ...doc, page: p }); onClose(); }} />
    </Modal>
  );
}

export function ShareDialog({ doc, config, act, onClose }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const add = () => { if (!email.includes("@")) return; act.share(email.trim(), role); setEmail(""); };
  return (
    <Modal title={`Share "${doc.title}"`} onClose={onClose} testid="studio-share" wide>
      <div className="flex gap-2">
        <input list="studio-people" data-testid="studio-share-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Add people by email" className={inp} />
        <datalist id="studio-people">{config.people.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}</datalist>
        <select data-testid="studio-share-role" value={role} onChange={(e) => setRole(e.target.value)} className={`${inp} w-32`}>{config.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
        <button data-testid="studio-share-add" onClick={add} className={primary}>Send</button>
      </div>
      <p className="text-slate-500 mt-4 mb-1">People with access</p>
      <div className="flex items-center gap-2 py-1.5"><span className="w-7 h-7 rounded-full bg-[#1a73e8] text-white text-xs flex items-center justify-center">Y</span><span className="flex-1">You</span><span className="text-slate-500">Owner</span></div>
      {doc.shared.map((p) => (
        <div key={p.email} className="flex items-center gap-2 py-1.5" data-testid={`studio-share-row-${p.email}`}>
          <span className="w-7 h-7 rounded-full bg-[#34a853] text-white text-xs flex items-center justify-center uppercase">{p.email[0]}</span>
          <span className="flex-1 truncate">{config.people.find((x) => x.email === p.email)?.name || p.email}<span className="block text-[11px] text-slate-500">{p.email}</span></span>
          <select value={p.role} data-testid={`studio-share-role-${p.email}`} onChange={(e) => act.share(p.email, e.target.value)} className="h-8 rounded border border-slate-300 text-[12px] px-1">{config.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
          <button data-testid={`studio-share-remove-${p.email}`} onClick={() => act.unshare(p.email)} className="text-slate-500 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex justify-end mt-4"><button onClick={onClose} className={primary} data-testid="studio-share-done">Done</button></div>
    </Modal>
  );
}

export function ImageDialog({ config, act, onClose }) {
  return (
    <Modal title="Insert image" onClose={onClose} testid="studio-image-dialog" wide>
      <p className="text-slate-500 mb-3">Choose a picture from the gallery. It is inserted after the selected line.</p>
      <div className="grid grid-cols-3 gap-3">
        {(config.gallery || []).map((g) => (
          <button key={g.id} data-testid={`studio-image-pick-${g.id}`} onClick={() => { act.insertImage(g.id); onClose(); }} className="rounded-lg overflow-hidden border border-slate-200 hover:border-[#1a73e8] text-left">
            <img src={g.url} alt={g.label} className="w-full h-20 object-cover" /><span className="block px-2 py-1 text-[12px]">{g.label}</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}

export function MoveDialog({ doc, config, act, onClose }) {
  const [f, setF] = useState(doc.folder || "My Files");
  return (
    <Modal title="Move to folder" onClose={onClose} testid="studio-move">
      {config.folders.map((x) => <label key={x} className="flex items-center gap-2 py-1.5"><input type="radio" data-testid={`studio-move-${x.replace(/\s+/g, "-").toLowerCase()}`} checked={f === x} onChange={() => setF(x)} className="accent-[#1a73e8]" /> <span className="text-[#5f6368]">📁</span> {x}</label>)}
      <Footer testid="studio-move" onClose={onClose} okLabel="Move" onOk={() => { act.setDoc({ ...doc, folder: f }); onClose(); }} />
    </Modal>
  );
}

export function PrintDialog({ doc, act, onClose }) {
  const [copies, setCopies] = useState(1);
  const [color, setColor] = useState("color");
  return (
    <Modal title="Print" onClose={onClose} testid="studio-print-dialog">
      <div className="grid grid-cols-2 gap-3">
        <label className="text-slate-500">Destination<select className={`${inp} mt-1`}><option>Classroom Printer</option><option>Save as PDF</option></select></label>
        <label className="text-slate-500">Copies<input type="number" min={1} max={30} value={copies} onChange={(e) => setCopies(Number(e.target.value))} className={`${inp} mt-1`} data-testid="studio-print-copies" /></label>
        <label className="text-slate-500">Pages<select className={`${inp} mt-1`}><option>All</option><option>Custom</option></select></label>
        <label className="text-slate-500">Color<select value={color} onChange={(e) => setColor(e.target.value)} className={`${inp} mt-1`}><option value="color">Color</option><option value="bw">Black and white</option></select></label>
      </div>
      <div className="mt-3 h-24 bg-slate-100 rounded flex items-center justify-center text-slate-500 text-[12px]">Preview · {wordStats(doc).pages} page(s) · {doc.title}</div>
      <Footer testid="studio-print-dialog" onClose={onClose} okLabel="Print" onOk={() => { act.setDoc({ ...doc, printed: true }); onClose(); }} />
    </Modal>
  );
}

export function WordCountDialog({ doc, onClose }) {
  const s = wordStats(doc);
  return (
    <Modal title="Word count" onClose={onClose} testid="studio-wordcount">
      {[["Pages", s.pages], ["Words", s.words], ["Characters", s.chars], ["Characters excluding spaces", s.charsNoSpace]].map(([k, v]) => <div key={k} className="flex justify-between py-1.5 border-b border-slate-100 last:border-0"><span className="text-slate-600">{k}</span><span data-testid={`studio-wordcount-${k.split(" ")[0].toLowerCase()}`}>{v}</span></div>)}
      <div className="flex justify-end mt-4"><button onClick={onClose} className={primary} data-testid="studio-wordcount-ok">OK</button></div>
    </Modal>
  );
}

export function DefineDialog({ word, config, onClose }) {
  const w = (word || "").trim().toLowerCase().replace(/[^a-z']/g, "");
  const def = config.dictionary?.[w];
  return (
    <Modal title="Dictionary" onClose={onClose} testid="studio-define">
      {w ? <><p className="text-[18px] text-slate-900" data-testid="studio-define-word">{w}</p><p className="mt-1 text-slate-700">{def || "No definition found in the class dictionary — try a science word from the text."}</p></> : <p className="text-slate-600">Select a word in the document first, then press Ctrl+Shift+Y.</p>}
      <div className="flex justify-end mt-4"><button onClick={onClose} className={primary} data-testid="studio-define-ok">Close</button></div>
    </Modal>
  );
}

export function VoiceDialog({ config, act, onClose }) {
  const [rec, setRec] = useState(false);
  const speak = () => { setRec(true); setTimeout(() => { act.voiceType(config.voiceSentence); setRec(false); onClose(); }, 1200); };
  return (
    <Modal title="Voice typing" onClose={onClose} testid="studio-voice">
      <p className="text-slate-600">Click the microphone and speak. (In this Studio the microphone is simulated — it types a practice sentence.)</p>
      <div className="flex justify-center my-4"><button data-testid="studio-voice-mic" onClick={speak} disabled={rec} className={`w-16 h-16 rounded-full flex items-center justify-center text-white ${rec ? "bg-[#d93025] animate-pulse" : "bg-[#5f6368] hover:bg-[#1a73e8]"}`}><Mic className="w-7 h-7" /></button></div>
      <p className="text-center text-slate-500 text-[12px]">{rec ? "Listening…" : "Click to speak"}</p>
    </Modal>
  );
}

export function VersionDialog({ doc, act, onClose }) {
  const [name, setName] = useState(doc.versionName || "");
  return (
    <Modal title="Version history" onClose={onClose} testid="studio-version">
      <div className="rounded border border-slate-200 p-2 mb-3 text-[12px]"><p className="font-medium">Current version</p><p className="text-slate-500">Today · You</p>{doc.versionName && <p className="text-[#1a73e8]">Named: {doc.versionName}</p>}</div>
      <label className="text-slate-500 block mb-1">Name current version</label>
      <input data-testid="studio-version-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. First draft" className={inp} />
      <Footer testid="studio-version" onClose={onClose} okLabel="Save" disabled={!name.trim()} onOk={() => { act.setDoc({ ...doc, versionName: name.trim() }); onClose(); }} />
    </Modal>
  );
}

export function LinkDialog({ selected, act, onClose }) {
  const [url, setUrl] = useState(selected?.fmt?.link || "https://www.");
  const ok = /^https?:\/\/\S+\.\S+/.test(url);
  return (
    <Modal title="Insert link" onClose={onClose} testid="studio-link-dialog">
      <p className="text-slate-500 mb-1">Text: <span className="text-slate-900">{selected?.text || "(selected line)"}</span></p>
      <input data-testid="studio-link-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.example.com" className={inp} autoFocus />
      {selected?.fmt?.link && <button data-testid="studio-link-remove" onClick={() => { act.setFmt("link", ""); onClose(); }} className="mt-2 text-[12px] text-red-600 hover:underline">Remove link</button>}
      <Footer testid="studio-link-dialog" onClose={onClose} okLabel="Apply" disabled={!ok} onOk={() => { act.setFmt("link", url.trim()); onClose(); }} />
    </Modal>
  );
}

export function CommentDialog({ selected, act, onClose }) {
  const [text, setText] = useState("");
  return (
    <Modal title="Add a comment" onClose={onClose} testid="studio-comment-dialog">
      <p className="text-[11px] text-slate-500 border-l-2 border-[#fbbc04] pl-2 mb-2 truncate">{selected?.text || selected?.caption || "(selected item)"}</p>
      <textarea data-testid="studio-comment-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Comment or add others with @" className={`${inp} h-auto py-2`} autoFocus />
      <Footer testid="studio-comment-dialog" onClose={onClose} okLabel="Comment" disabled={!text.trim()} onOk={() => { act.addComment(text.trim()); onClose(); }} />
    </Modal>
  );
}

export function ShortcutsDialog({ onClose }) {
  return (
    <Modal title="Keyboard shortcuts" onClose={onClose} testid="studio-shortcuts">
      <div className="grid grid-cols-2 gap-x-6">{SHORTCUTS.map(([k, v]) => <div key={k} className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-700">{k}</span><kbd className="text-[11px] bg-slate-100 rounded px-1.5">{v}</kbd></div>)}</div>
    </Modal>
  );
}

export function HelpDialog({ onClose }) {
  return (
    <Modal title="Docs Help" onClose={onClose} testid="studio-help">
      <ul className="list-disc pl-5 space-y-1 text-slate-700">
        <li><b>File</b> — name, move, download, print and set up the page.</li><li><b>Edit</b> — undo, redo, find and replace.</li><li><b>View</b> — mode, ruler, outline.</li>
        <li><b>Insert</b> — images, tables, links, comments, page breaks, headers, table of contents.</li><li><b>Format</b> — text, paragraph styles, alignment, spacing, lists.</li>
      </ul>
    </Modal>
  );
}
